import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgePercent, ShoppingBag, Ticket, TrendingUp, UserPlus, Wallet } from "lucide-react";
import { toast } from "sonner";

import Cabecalho from "../comum/Cabecalho";
import Carregando from "../comum/Carregando";
import FiltroPeriodo from "../comum/FiltroPeriodo";
import GraficoDias from "../comum/GraficoDias";
import Kpi from "../comum/Kpi";
import SemanaHora from "../comum/SemanaHora";
import Selo from "../comum/Selo";
import { num, pct, qtdItem, reais } from "../comum/formato";
import { useFiltros } from "../comum/useFiltros";
import { ROTA_CUPONS } from "../rotas";
import Alertas from "./Alertas";
import { carregarCupom } from "./api";
import PedidosDoCupom from "./PedidosDoCupom";
import Ranking from "./Ranking";
import RegrasCupom from "./RegrasCupom";

// Relatório de um cupom (Relatório de Cupons → clique no cupom).
export default function CupomDetalhePage() {
  const { chave } = useParams();
  const { filtros, mudar, query, paraApi } = useFiltros();
  const [rel, setRel] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    // Trocar período/filtro mantém o relatório na tela até o novo chegar (25/10/2026)
    setErro(false);
    carregarCupom(decodeURIComponent(chave), { date_from: paraApi.date_from, date_to: paraApi.date_to, modulo: paraApi.modulo })
      .then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar o cupom"); } });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, paraApi.date_from, paraApi.date_to, paraApi.modulo]);

  const c = rel?.cupom;
  // Carregando: o título pisca em vez de mostrar o id interno do cupom.
  const r = rel?.resumo || {};
  const h = rel?.historico || {};
  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-5" data-e2e="detalhe-cupom">
        <Cabecalho voltarPara={`${ROTA_CUPONS}${query({ busca: "" })}`} voltarRotulo="Relatório de Cupons" icone={Ticket} corIcone="bg-sky-500/10 text-sky-500"
          titulo={!rel && !erro ? <span className="inline-block h-7 w-48 max-w-full align-middle animate-pulse rounded-md bg-primary/10" aria-label="Carregando" /> : <span className="font-mono tracking-wide">{c?.codigo || decodeURIComponent(chave).replace("codigo:", "")}</span>}
          subtitulo={c && (
            <span className="flex flex-wrap items-center gap-1.5">
              <Selo status={c.status} rotulo={c.status_rotulo} />
              {c.existe && <span>{c.valor_rotulo} · {c.categoria_rotulo}</span>}
              {c.descricao && <span className="text-muted-foreground">· {c.descricao}</span>}
            </span>
          )} />
        <FiltroPeriodo de={filtros.de} ate={filtros.ate} onMudar={mudar} cor="bg-sky-500 text-white" />

        {erro ? <p className="text-sm text-muted-foreground">Cupom não encontrado.</p> : !rel ? <Carregando /> : (
          <>
            <RegrasCupom cupom={c} tempo={rel.linha_do_tempo} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi rotulo="Pedidos no período" valor={r.pedidos} dica={`${h.pedidos} desde a criação`} icone={ShoppingBag} e2e="kpi-cupom-pedidos" />
              <Kpi rotulo="Desconto concedido" valor={reais(r.desconto)} dica={`${reais(h.desconto)} no total`} icone={BadgePercent} />
              <Kpi rotulo="Faturado" valor={reais(r.faturado)} dica={`ticket ${reais(r.ticket_medio)}`} icone={Wallet} destaque="bg-sky-500/10 border-sky-500/30" />
              <Kpi rotulo="Lucro bruto" valor={r.lucro == null ? "—" : reais(r.lucro)} dica={r.lucro == null ? "sem ficha técnica" : `${pct(r.margem_pct)} margem`} icone={TrendingUp} />
              <Kpi rotulo="Retorno do desconto" valor={r.retorno ? reais(r.retorno) : "—"} dica="em vendas por R$ 1" icone={TrendingUp} />
              <Kpi rotulo="Clientes" valor={r.clientes} dica={`${r.clientes_novos} no primeiro pedido`} icone={UserPlus} />
              <Kpi rotulo="Faturado desde a criação" valor={reais(h.faturado)} dica={`${h.pedidos} pedidos`} icone={Wallet} />
              <Kpi rotulo="Lucro desde a criação" valor={h.lucro == null ? "—" : reais(h.lucro)} dica={h.lucro == null ? "sem ficha técnica" : `${pct(h.margem_pct)} margem`} icone={TrendingUp} />
            </div>
            <Alertas alertas={rel.alertas} />
            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <GraficoDias serie={rel.por_dia} principal="faturado" secundaria="desconto" titulo="Uso por dia"
                  legenda={[{ rotulo: "Faturado", cor: "bg-sky-500" }, { rotulo: "Desconto", cor: "bg-amber-400" }]} />
              </div>
              <div className="lg:col-span-2"><SemanaHora semana={rel.semana} hora={rel.hora} titulo="Quando é usado" /></div>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <Ranking titulo="O que compraram com este cupom" itens={rel.produtos} rotulo={(p) => p.nome} valor={(p) => p.valor}
                detalhe={(p) => `${qtdItem(p.qtd, p.unidade)} · ${reais(p.valor)}`} vazio="Sem pedidos no período." e2e="produtos-cupom" />
              <Ranking titulo="Clientes que mais usaram" itens={rel.clientes} rotulo={(p) => p.nome || "Sem nome"} valor={(p) => p.usos}
                detalhe={(p) => `${p.usos}x · ${reais(p.faturado)}`} vazio="Sem pedidos no período." />
            </div>
            <PedidosDoCupom pedidos={rel.pedidos} />
          </>
        )}
      </div>
    </div>
  );
}
